/**
 * GA4 페이지뷰 가드 — 라우터에 afterEach 를 걸어 SPA 가상 페이지뷰를 보낸다.
 *
 * `registerAuthGuard` 와 같은 `(router) => void` 시그니처를 유지해 main.ts 등록부의 대칭을 맞춘다.
 * router/index.ts 에 합치지 않는 이유는 그 파일이 "routes·scrollBehavior·registerAuthGuard 3개 export"를
 * 파일 JSDoc 으로 계약해 두고 있어서다 — 계측을 끼워 넣으면 그 계약이 깨진다.
 *
 * ## beforeEach 가 아니라 afterEach 인 이유
 * beforeEach 는 가드가 리다이렉트로 취소할 수 있는 시점이라 **도달하지 않은** 페이지의 뷰가 찍힌다.
 * 미인증 상태로 보호 라우트에 들어가면 error-auth-required 로 튕기는데, 여기서 그 화면 뷰가 기록되면
 * 유입 퍼널이 왜곡된다. afterEach 는 내비게이션 확정 후라 실제 도달한 화면만 남는다.
 */
import type { Router } from 'vue-router';

import { trackPageView } from '#/lib/analytics';

/** title 갱신을 기다리는 상한(ms). 타이틀을 바꾸지 않는 화면도 있으므로 무한정 대기하지 않는다. */
const TITLE_SETTLE_TIMEOUT_MS = 300;

/**
 * GA4 로 내보내도 되는 쿼리 파라미터 — 유입 분석에 실제로 쓰는 마케팅 파라미터만 열거한다.
 *
 * 화이트리스트인 것이 핵심이다. 블랙리스트로 두면 새 파라미터가 생길 때마다 누락돼 유출된다.
 */
const ALLOWED_QUERY_KEYS = new Set([
  'gclid',
  'fbclid',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
]);

/**
 * 계측용 경로 — 마케팅 파라미터만 남기고 나머지 쿼리를 제거한다.
 *
 * `fullPath` 를 그대로 보내면 쿼리에 실린 식별자·토큰이 Google 로 전송될 수 있다.
 * 유입 분석에 필요한 건 utm·클릭ID 뿐이므로 그 외는 값이 아니라 키까지 버린다 — 허용 목록 방식이라
 * 앞으로 어떤 쿼리 파라미터가 생겨도 기본이 '보내지 않음'이다.
 */
function toSafePath(fullPath: string): string {
  const [path, queryString] = fullPath.split('?');
  if (!queryString) return path;

  const kept = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(queryString)) {
    if (ALLOWED_QUERY_KEYS.has(key)) kept.append(key, value);
  }
  const keptString = kept.toString();
  return keptString ? `${path}?${keptString}` : path;
}

/**
 * `document.title` 이 실제로 바뀔 때까지(또는 상한까지) 기다린다.
 *
 * afterEach 는 unhead 가 title 을 반영하기 전에 실행되고, 반영은 nextTick 한 번으로도 끝나지 않는다
 * (실측 ~27ms). 그대로 보내면 모든 페이지가 직전 화면의 타이틀로 기록돼 GA4 리포트에서 페이지를
 * 구분할 수 없다. 고정 지연으로 맞추면 기기·네트워크에 따라 깨지므로 변경 자체를 감지한다.
 */
function afterTitleSettles(previousTitle: string, send: () => void): void {
  let done = false;
  const finish = (): void => {
    if (done) return;
    done = true;
    observer.disconnect();
    clearTimeout(timer);
    send();
  };

  const observer = new MutationObserver(() => {
    if (document.title !== previousTitle) finish();
  });
  const titleNode = document.querySelector('title');
  if (titleNode) observer.observe(titleNode, { childList: true });

  // 타이틀이 그대로인 화면(또는 <title> 부재)에서도 페이지뷰는 반드시 나가야 한다.
  const timer = setTimeout(finish, TITLE_SETTLE_TIMEOUT_MS);
}

/**
 * 라우터에 페이지뷰 계측을 등록한다. 브라우저에서만 호출한다(main.ts isClient 블록).
 * GA4 측정 ID 가 없으면 trackPageView 가 no-op 이므로 dev 에서는 아무 일도 하지 않는다.
 */
export function registerAnalytics(router: Router): void {
  router.afterEach((to) => {
    // 순수 redirect 라우트는 name 이 없다. afterEach 는 최종 목적지만 관측하므로 실무상 도달하지 않지만,
    // name 이 symbol 일 수도 있어 문자열이 아니면 'unknown' 으로 떨어뜨린다.
    const screen = typeof to.name === 'string' ? to.name : 'unknown';
    const previousTitle = document.title;
    // 자격증명이 섞인 쿼리는 여기서 걸러 낸다 — GA4 로는 마케팅 파라미터만 나간다.
    const safePath = toSafePath(to.fullPath);
    afterTitleSettles(previousTitle, () => {
      trackPageView(screen, safePath);
    });
  });
}
